import { useEffect, useRef, useState } from 'react';

/**
 * Observe a single element's intersection with the viewport.
 *
 * @param {object}  options
 * @param {boolean} options.once   stop observing after the first entry (default true)
 * @param {string}  options.margin rootMargin passed to the observer
 * @param {number}  options.amount visible fraction that counts as "in view"
 * @returns {[React.RefObject, boolean]}
 */
export function useInView({ once = true, margin = '0px 0px -12% 0px', amount = 0.15 } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // Without IntersectionObserver, show everything rather than nothing.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin: margin, threshold: amount }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, margin, amount]);

  return [ref, inView];
}

export default useInView;
